import { Children, type ComponentPropsWithoutRef, forwardRef, isValidElement, type ReactNode } from 'react';
import { TextArea } from '@zep-us/zus';
import { cn } from '@/utils/tailwind.util';
import { AiBuilderFileDropOverlay } from './AiBuilderFileDropOverlay';

// drop 오버레이 높이 — composer 입력창 바닥을 기준으로 위로 확장한다
// (시안 39062:34875, 제안칩+입력창 영역 ≈ 266px).
const DROP_OVERLAY_HEIGHT = 266;

// 시안(37178:108809) 기준 그림자는 strong border(outline)와 함께 켜진다 —
// Default(비포커스) 상태는 border-alternative만 있고 그림자가 없다.
const SHELL_WRAPPER_CLASS = 'relative flex-col items-stretch gap-0 rounded-md border-2 !px-0 !py-0 focus-within:!border-strong-default focus-within:!ring-0 focus-within:shadow-sm';
const SHELL_SCROLL_AREA_CLASS = 'relative flex max-h-[230px] w-full flex-col gap-xs overflow-y-auto overflow-x-hidden pb-xs pl-md pr-sm pt-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
const SHELL_BOTTOM_BAR_CLASS = 'flex min-h-[56px] w-full items-end justify-between px-md pb-md pt-sm';
type AiBuilderComposerShellProps = {
  intent: 'neutral' | 'strong';
  value: string;
  dropOverlayVisible?: boolean;
  muted?: boolean;
  children: ReactNode;
};
const TopSlot = ({
  children
}: {
  children: ReactNode;
}) => <>{children}</>;
const AttachmentSlot = ({
  children
}: {
  children: ReactNode;
}) => <>{children}</>;
type FieldProps = ComponentPropsWithoutRef<typeof TextArea.InputField> & {
  sourceInputTooltip?: {
    label: string;
    open: boolean;
  };
};
const Field = forwardRef<HTMLTextAreaElement, FieldProps>(({
  sourceInputTooltip,
  ...props
}, ref) => {
  return <TextArea.InputField ref={ref} {...props} />;
});
Field.displayName = 'AiBuilderComposerShellField';
const BottomBar = ({
  children
}: {
  children: ReactNode;
}) => <>{children}</>;
export const AiBuilderComposerShell = ({
  children,
  dropOverlayVisible,
  intent,
  muted,
  value
}: AiBuilderComposerShellProps) => {
  let topSlot: ReactNode = null;
  let attachmentSlot: ReactNode = null;
  let field: ReactNode = null;
  let sourceInputTooltip: FieldProps['sourceInputTooltip'] = undefined;
  let bottomBar: ReactNode = null;
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    if (child.type === TopSlot) topSlot = child;else if (child.type === AttachmentSlot) attachmentSlot = child;else if (child.type === Field) {
      field = child;
      sourceInputTooltip = child.props.sourceInputTooltip;
    } else if (child.type === BottomBar) bottomBar = child;
  }
  return <TextArea.Root required={false} value={value} data-sentry-element="unknown" data-sentry-component="AiBuilderComposerShell" data-sentry-source-file="AiBuilderComposerShell.tsx">
      <TextArea.InputWrapper intent={intent} data-ai-builder-composer-input-wrapper className={cn(SHELL_WRAPPER_CLASS, muted ? 'bg-alternative' : 'bg-default')} data-sentry-element="unknown" data-sentry-source-file="AiBuilderComposerShell.tsx">
        {dropOverlayVisible && <div className="absolute inset-x-0 bottom-0 z-[70]" style={{
        height: DROP_OVERLAY_HEIGHT
      }}>
            <AiBuilderFileDropOverlay />
          </div>}
        {sourceInputTooltip?.open && <div className="pointer-events-none absolute left-md top-sm z-[10000] -translate-y-[calc(100%+10px)] rounded-sm bg-[rgba(28,27,31,0.8)] px-sm py-2xs text-body-md-semibold text-inverse shadow-md">
            {sourceInputTooltip.label}
            <span className="absolute bottom-[-6px] left-sm size-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[rgba(28,27,31,0.8)]" />
          </div>}
        {attachmentSlot ? <div className="w-full overflow-x-auto px-sm pt-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {attachmentSlot}
          </div> : null}
        <div className={SHELL_SCROLL_AREA_CLASS} data-ai-builder-composer-input-header>
          {topSlot}
          {field}
        </div>

        <div className={SHELL_BOTTOM_BAR_CLASS}>{bottomBar}</div>
      </TextArea.InputWrapper>
    </TextArea.Root>;
};
AiBuilderComposerShell.TopSlot = TopSlot;
AiBuilderComposerShell.AttachmentSlot = AttachmentSlot;
AiBuilderComposerShell.Field = Field;
AiBuilderComposerShell.BottomBar = BottomBar;